// ** react imports
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

// ** router imports
import { useNavigate } from 'react-router-dom';

// ** utils imports
import { formatUtcToLocal } from '../../../utils/Utils';

// ** enums imports
import { USER_ROLES } from '../../../enums/common';

function UsersTableItem(props) {
  // props
  const { data, isSelected } = props;

  // states for dropdown menu
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // hooks
  const navigate = useNavigate();

  // refs
  const trigger = useRef(null);
  const dropdown = useRef(null);

  // close dropdown on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!dropdown.current) return;
      if (
        !dropdownOpen ||
        dropdown.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setDropdownOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  });

  // close dropdown on escape key
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  // Helper functions
  const formatPhoneNumber = (countryCode, phone) => {
    if (!phone) return '-';
    const raw = String(phone).trim();
    const digits = raw.replace(/\D/g, '');

    // Handle US-style formatting
    let formatted = raw;
    if (digits.length === 10) {
      formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else if (digits.length === 11 && digits.startsWith('1')) {
      const d = digits.slice(1);
      formatted = `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
      // Default to +1 if not provided
      if (!countryCode) countryCode = '+1';
    }

    return countryCode ? `${countryCode} ${formatted}` : formatted;
  };

  const getRoleBadge = role => {
    switch (role) {
      case USER_ROLES.ADMIN:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400">
            Administrator
          </span>
        );
      case USER_ROLES.MANAGER:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
            Manager
          </span>
        );
      case USER_ROLES.CUSTOMER:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
            Customer
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
            {role || 'Unknown'}
          </span>
        );
    }
  };

  const getStatusBadge = () => {
    const isVerified = data?.isEmailVerified && data?.isPasswordSet;
    const isActive = data?.isActive;

    if (!isActive) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400">
          Inactive
        </span>
      );
    }

    if (isVerified) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
          Verified
        </span>
      );
    }

    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400">
        Pending
      </span>
    );
  };

  return (
    <tr
      className={`${
        isSelected ? 'bg-violet-50 dark:bg-violet-500/20' : ''
      } hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors cursor-pointer`}
      onClick={() => navigate(`/users/details/${data?._id}?page=profile`)}
    >
      {/* User ID */}
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
        <div className="text-left font-medium text-violet-600 dark:text-violet-400 cursor-pointer hover:text-violet-700 dark:hover:text-violet-300 hover:underline transition-colors">
          #{data?.userId || 'N/A'}
        </div>
      </td>

      {/* Full Name */}
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
        <div className="flex items-center">
          <div className="w-8 h-8 bg-violet-100 dark:bg-violet-500/25 rounded-full flex items-center justify-center mr-3">
            <span className="text-sm font-medium text-violet-600 dark:text-violet-300">
              {data?.fullName ? data.fullName.charAt(0).toUpperCase() : '?'}
            </span>
          </div>
          <div className="font-medium text-gray-800 dark:text-gray-100">
            {data?.fullName || 'N/A'}
          </div>
        </div>
      </td>

      {/* Email */}
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
        <div className="flex items-center">
          <div className="text-left text-gray-800 dark:text-gray-100">
            {data?.email || 'N/A'}
          </div>
          {data?.isEmailVerified && data?.isPasswordSet && (
            <svg
              className="w-4 h-4 text-green-500 ml-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </div>
      </td>

      {/* Phone Number */}
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
        <div className="text-center text-gray-800 dark:text-gray-100">
          {formatPhoneNumber(data?.countryCode, data?.phone)}
        </div>
      </td>

      {/* Role */}
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
        {getRoleBadge(data?.role)}
      </td>

      {/* Status */}
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
        {getStatusBadge()}
      </td>

      {/* Created Date */}
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
        <div className="text-gray-800 dark:text-gray-100">
          {formatUtcToLocal(data?.createdAt)}
        </div>
      </td>
    </tr>
  );
}

// PropTypes for better type checking
UsersTableItem.propTypes = {
  data: PropTypes.object.isRequired,
  isSelected: PropTypes.bool,
};

export default UsersTableItem;
